import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  CheckCircleIcon,
  SlidersHorizontalIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ChatSessionMenu } from '@/components/chat/ChatSessionMenu';
import { CreatePlanDialog } from '@/components/plans/CreatePlanDialog';
import { Confirm } from '@/components/ui/confirm';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useResolveCreateParams } from '@/hooks/create/useResolveCreateParams';
import { useAuth } from '@/hooks/use-auth';
import { peekDiscoverSessionId } from '@/lib/chat/discover-session-id';
import { ROW_HOVER } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { campaignService } from '@/services/data/CampaignService';
import { logger } from '@/utils/logger';

interface BreadcrumbSwitcherProps {
  /** The trigger node (the breadcrumb title leaf). */
  children: React.ReactNode;
}

/**
 * Mounts a context-menu dropdown attached to the breadcrumb caret. The menu
 * surfaces conversation / plan actions (Star · Rename · Move · Delete) per the
 * Claude.ai pattern, replacing the prior recent-chats dialog + plan-list
 * popover surfaces.
 *
 * Behavior matrix:
 *
 *   - On `/` (no planId)   → conversation actions on the active session.
 *   - On `/?planId=X`      → conversation actions tied to the linked plan.
 *   - On `/projects/:id`      → plan actions on the campaign.
 */
export function BreadcrumbSwitcher({ children }: BreadcrumbSwitcherProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const planIdParam = searchParams.get('planId');
  const createParams = useResolveCreateParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isCreateRoute = location.pathname === '/';
  const isPlanDetail =
    /^\/plans\/[^/?#]+/.test(location.pathname) || /^\/projects\/[^/?#]+/.test(location.pathname);
  const planIdForPlanDetail = isPlanDetail ? params.projectId : undefined;

  // Fetch the active campaign for edit-details prefill (only on plan-detail route).
  const { data: activeCampaign } = useQuery({
    queryKey: ['campaign', planIdForPlanDetail],
    queryFn: async () => {
      if (!planIdForPlanDetail) return null;
      const res = await campaignService.getCampaign(planIdForPlanDetail);
      return res.data ?? null;
    },
    enabled: !!planIdForPlanDetail,
    staleTime: 30_000,
  });

  const sessionId = isCreateRoute
    ? (createParams.resumeSession ?? peekDiscoverSessionId() ?? null)
    : null;
  const linkedPlanId = isCreateRoute && planIdParam ? planIdParam : null;
  const planIdForActions = planIdForPlanDetail ?? linkedPlanId ?? null;
  const onPlanRoute = isPlanDetail;

  const handleRequestDelete = (): void => {
    setOpen(false);
    if (!planIdForActions) {
      toast.error('No plan to delete.');
      return;
    }
    setConfirmDeleteOpen(true);
  };

  const refreshProjectLists = (): void => {
    queryClient.invalidateQueries({ queryKey: ['campaigns', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['campaign', planIdForActions] });
    queryClient.invalidateQueries({ queryKey: ['archive-items'] });
  };

  const handleUndoArchive = async (): Promise<void> => {
    if (!planIdForActions) return;
    const result = await campaignService.restoreProject(planIdForActions);
    if (result.error) {
      logger.error('[BreadcrumbSwitcher] undo archive failed', result.error);
      toast.error('Could not restore project.');
      return;
    }
    refreshProjectLists();
  };

  const handleArchive = async (): Promise<void> => {
    setOpen(false);
    if (!planIdForActions) {
      toast.error('No project to archive.');
      return;
    }
    const result = await campaignService.archiveProject(planIdForActions);
    if (result.error) {
      logger.error('[BreadcrumbSwitcher] archive plan failed', result.error);
      toast.error('Could not archive project.');
      return;
    }
    refreshProjectLists();
    navigate('/projects');
    toast.success('Project archived.', {
      action: { label: 'Undo', onClick: () => void handleUndoArchive() },
    });
  };

  const handleSetStatus = async (
    nextStatus: 'completed' | 'active',
    successMessage: string,
  ): Promise<void> => {
    setOpen(false);
    if (!planIdForActions) {
      toast.error('No project selected.');
      return;
    }
    const result =
      nextStatus === 'completed'
        ? await campaignService.completeProject(planIdForActions)
        : activeCampaign?.archivedAt || activeCampaign?.status === 'archived'
          ? await campaignService.restoreProject(planIdForActions)
          : await campaignService.reopenProject(planIdForActions);
    if (result.error) {
      logger.error('[BreadcrumbSwitcher] update plan status failed', result.error);
      toast.error('Could not update project status.');
      return;
    }
    refreshProjectLists();
    toast.success(successMessage);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!planIdForActions) throw new Error('No plan to delete.');
    const result = await campaignService.deleteCampaign(planIdForActions);
    if (result.error) {
      logger.error('[BreadcrumbSwitcher] delete plan failed', result.error);
      throw new Error('Could not delete plan.');
    }
    queryClient.invalidateQueries({ queryKey: ['campaigns', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['campaign', planIdForActions] });
    toast.success('Project deleted.');
    navigate('/projects');
  };

  const triggerButton = (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm px-1 -mx-1 outline-none',
        'focus-visible:ring-1 focus-visible:ring-ring/50',
        ROW_HOVER,
      )}
      aria-haspopup="menu"
      aria-label={onPlanRoute ? 'Plan actions' : 'Conversation actions'}
    >
      {children}
    </button>
  );

  // Conversation actions delegate to the shared ChatSessionMenu so the
  // breadcrumb and the sidebar recent-chats rows never drift.
  if (!onPlanRoute) {
    return (
      <ChatSessionMenu sessionId={sessionId} linkedPlanId={linkedPlanId} align="start">
        {triggerButton}
      </ChatSessionMenu>
    );
  }

  const isArchived = Boolean(activeCampaign?.archivedAt) || activeCampaign?.status === 'archived';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="w-[210px]">
        {isArchived ? (
          <DropdownMenuItem
            onSelect={() => void handleSetStatus('active', 'Project restored from archive.')}
          >
            <ArrowCounterClockwiseIcon
              className="size-3.5 text-muted-foreground/70"
              weight="regular"
            />
            <span>Restore from archive</span>
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem
              onSelect={() => {
                setOpen(false);
                setEditDetailsOpen(true);
              }}
            >
              <SlidersHorizontalIcon
                className="size-3.5 text-muted-foreground/70"
                weight="regular"
              />
              <span>Edit details</span>
            </DropdownMenuItem>
            {activeCampaign?.status === 'completed' ? (
              <DropdownMenuItem
                onSelect={() => void handleSetStatus('active', 'Project reopened.')}
              >
                <ArrowCounterClockwiseIcon
                  className="size-3.5 text-muted-foreground/70"
                  weight="regular"
                />
                <span>Reopen project</span>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => void handleSetStatus('completed', 'Project closed.')}
              >
                <CheckCircleIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
                <span>Close project</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => void handleArchive()}>
              <ArchiveIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
              <span>Archive</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={handleRequestDelete}
              className="text-destructive focus:text-destructive"
            >
              <TrashIcon className="size-3.5" weight="regular" />
              <span>Delete</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
      <Confirm
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        variant="destructive"
        title="Delete project?"
        description={`This will permanently remove "${activeCampaign?.name ?? 'this project'}". This action can't be undone.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
      />
      {planIdForActions && (
        <CreatePlanDialog
          open={editDetailsOpen}
          onOpenChange={setEditDetailsOpen}
          mode="edit"
          planId={planIdForActions}
          initialName={activeCampaign?.name ?? ''}
          initialGoal={activeCampaign?.goal ?? ''}
          initialStrategy={activeCampaign?.strategy ?? undefined}
          initialIcon={activeCampaign?.icon ?? null}
          initialColor={activeCampaign?.color ?? null}
          initialBrandVoiceId={activeCampaign?.brandVoiceId ?? null}
          initialBrandKitId={activeCampaign?.brandKitId ?? null}
        />
      )}
    </DropdownMenu>
  );
}

export default BreadcrumbSwitcher;
